/* eslint-disable no-console */
import {
  getOportunidades,
  getIntermediarios,
  getEdificiosOperativos,
  getClientes,
  getContratos,
  getRateForOpp,
} from ".";
import { cargarCobroPorVenta } from "./cobro/cargarCobroVentas";
import { cargarSnapshotComision, cargarSnapshotPctCongelado } from "./comisionSnapshot";
import { cargarCostosIntermediacion, getTakeLocal } from "./costosIntermediacion";
import { getEmpleados } from "./empleados";
import { loadExchangeRates, getDolarValue, getDolarFallback } from "./exchangeRateService";
import { materializarComisionesCobradas } from "./materializarComisiones";

// Auto-materialización de comisiones cobradas (Prod-1a). Corre al montar el módulo RRHH (admin-only)
// para una ventana reciente de meses de COBRO. Idempotente por (empleado, referencia venta) → cero
// doble-pago: solo escribe cobros nuevos. El corte (COMISION_CORTE) lo aplica materializar.
//
// Reusa las funciones REALES (getSalesCommissionBreakdown vía materializar/detalleLiquidableDelMes,
// getCobroReal vía cargarCobroPorVenta, getRateForOpp/getTakeLocal). Solo replica el armado de datos
// de mis-facturas (pauta + contratos, país-scopeado). BTL fuera: no tiene overlay de cobro → nunca
// liquidable.

// Ventana de meses de cobro: mes en curso + los 2 anteriores. Exportada para test.
export const ventanaCobros = (hoy = new Date()) => {
  const out = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    out.push({ año: d.getFullYear(), mes: d.getMonth() + 1 });
  }
  return out;
};

// Arma los deps de comisión (los mismos que la card LIQUIDABLE) para un país, con datos vivos.
const cargarDeps = async (pais) => {
  await loadExchangeRates();
  const [sales, intermediarios, edificiosArr, clientesArr, contratos, snap, pct, costos, empleados] = await Promise.all(
    [
      getOportunidades({ pais }),
      getIntermediarios({ pais }),
      getEdificiosOperativos({ pais }),
      getClientes({ pais }),
      getContratos({ pais }),
      cargarSnapshotComision(pais).catch(() => null),
      cargarSnapshotPctCongelado(pais).catch(() => null),
      cargarCostosIntermediacion(pais).catch(() => null),
      getEmpleados(pais),
    ]
  );
  const cobroPorVenta = await cargarCobroPorVenta(pais).catch(() => new Map());

  const clientesMap = Object.fromEntries((clientesArr || []).map((c) => [c.id, c]));
  const intermediariosMap = Object.fromEntries((intermediarios || []).map((i) => [i.id, i]));
  const edificiosMap = Object.fromEntries((edificiosArr || []).map((e) => [e.id, e]));
  const empleadoPorUsuario = Object.fromEntries(
    (empleados || []).filter((e) => e.usuario_id != null).map((e) => [String(e.usuario_id), e.id])
  );

  const pauta = (sales || []).map((op) => ({
    ...op,
    ejecutivo: op.ejecutivo && op.ejecutivo !== "null" && op.ejecutivo !== "" ? op.ejecutivo : null,
    isBTL: false,
  }));
  const contratoRows = (contratos || [])
    .filter((c) => c.estatus === "100%" && !c.eliminada)
    .flatMap((c) =>
      (c.meses || []).map((m, idx) => {
        const [mesStr, anoStr] = (m.mes || "01-2026").split("-");
        return {
          id: `contrato-${c.id}-${idx}`,
          contrato_id: c.id,
          mes_index: idx,
          type: "contrato",
          client_id: c.client_id,
          cliente: clientesMap[c.client_id]?.nombre || c.cliente || "Desconocido",
          ejecutivo: c.ejecutivo ? String(c.ejecutivo) : null,
          pais: c.pais,
          importe: m.importe || 0,
          moneda_dolar: c.moneda_dolar || false,
          isBTL: false,
          facturafecha: m.facturafecha,
          mes: mesStr,
          ano: anoStr,
          mes_cierre: c.mes_cierre,
          estatus: "100%",
          eliminada: false,
          edificiosparticipantes: m.edificios_ids || "",
          pantallastotales: m.pantallas_totales || 0,
          pantallas_ascensores: m.pantallas_ascensores || "",
          pantallas_espera: m.pantallas_espera || "",
          pantallas_circulacion: m.pantallas_circulacion || "",
          pantallas_cowork: m.pantallas_cowork || "",
          apx_id: c.apx_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
        };
      })
    );
  const oportunidades = [...pauta, ...contratoRows].map((op) => {
    let intermediarioId = null;
    if (op.client_id) {
      const cl = clientesMap[op.client_id];
      if (cl && cl.agencia) intermediarioId = parseInt(cl.agencia, 10);
    }
    return { ...op, intermediarioId };
  });

  const gdv = (f) => getDolarValue(f, pais);
  const fallback = getDolarFallback(pais);
  return {
    oportunidades,
    clientes: clientesMap,
    intermediarios: intermediariosMap,
    edificios: edificiosMap,
    snapMap: snap,
    pctMap: pct,
    cobroPorVenta,
    empleadoPorUsuario,
    getRate: (op) => getRateForOpp(op, gdv, fallback),
    getTake: (op) => getTakeLocal(op, "sales", op.id, costos, gdv, fallback),
  };
};

// Corre la materialización para la ventana reciente. Devuelve el total de comisiones nuevas escritas.
// No lanza (degrada a 0 ante error de carga) para no romper el montaje del módulo.
export const autoMaterializarComisionesRRHH = async (pais, hoy = new Date()) => {
  try {
    const deps = await cargarDeps(pais);
    let insertados = 0;
    for (const { año, mes } of ventanaCobros(hoy)) {
      // eslint-disable-next-line no-await-in-loop
      const r = await materializarComisionesCobradas(año, mes, deps);
      insertados += r.insertados.length;
    }
    if (insertados > 0) console.log(`[RRHH] Auto-materialización: ${insertados} comisión(es) nueva(s) (${pais}).`);
    return insertados;
  } catch (e) {
    console.error("[RRHH] Auto-materialización falló:", e);
    return 0;
  }
};
